import { Box, Typography, Tooltip, useTheme } from "@mui/material";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { LuDownload, LuFileText, LuInbox } from "react-icons/lu";
import { FiTrash2, FiUpload } from "react-icons/fi";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { fetchWithToken } from "../authentication/AuthProvider";
import config from "../config";
import AgGrid from "../components/grid/AgGrid";
import {
  defaultColDef,
  leftAlignColDef,
  rightAlignColDef,
  centerAlignColDef,
} from "../components/grid/columnDefinitions/columnDefinitions";
import { useContextProvider } from "../components/page-context/ContextProvider";
import { DataTableCard, M3Button, M3IconButton, M3Badge, Chip } from "../components/m3";
import EvidenceBillBatchAnalyzerModal from "../components/dialog/EvidenceBillBatchAnalyzerModal";

const ACCEPTED_TYPES = ".pdf,.csv,.xlsx,.doc,.docx,.jpg,.jpeg,.png";
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

const formatUploadedAt = (uploadedAt) => {
  if (!uploadedAt) return "";
  try {
    const dateStr =
      uploadedAt.endsWith("Z") ||
      uploadedAt.includes("+") ||
      (uploadedAt.includes("-") && uploadedAt.includes("T"))
        ? uploadedAt
        : uploadedAt.includes("T")
        ? `${uploadedAt}Z`
        : `${uploadedAt.replace(" ", "T")}Z`;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return uploadedAt;
    }
    return date.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return uploadedAt;
  }
};

const SCOPE_FILTERS = ["All", "Scope 1", "Scope 2", "Scope 3", "Overview"];

function DocumentViewer({ pageType, selectedRow }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { t } = useTranslation();
  const { enqueueAlert, setRefresh } = useContextProvider();

  const [fileList, setFileList] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isBatchAnalyzerOpen, setIsBatchAnalyzerOpen] = useState(false);

  const gridRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchFiles = useCallback((id) => {
    if (!id) {
      setFileList([]);
      return;
    }
    fetchWithToken(`${config.apiUrl}/api/fetch_emission_reference_files?ID=${id}`)
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setFileList(list);
      })
      .catch((err) => {
        console.error("Fetch evidence files error:", err);
      });
  }, []);

  useEffect(() => {
    if (selectedRow?.id) {
      fetchFiles(selectedRow.id);
    }
  }, [selectedRow?.id, fetchFiles]);

  const counts = useMemo(() => {
    const res = { All: fileList.length, "Scope 1": 0, "Scope 2": 0, "Scope 3": 0, Overview: 0 };
    fileList.forEach((f) => {
      const s = String(f.scope || "").toLowerCase();
      if (s.includes("scope 1")) res["Scope 1"]++;
      else if (s.includes("scope 2")) res["Scope 2"]++;
      else if (s.includes("scope 3")) res["Scope 3"]++;
      else res.Overview++;
    });
    return res;
  }, [fileList]);

  const rowData = useMemo(() => {
    if (selectedFilter === "All") return fileList;
    const filterLower = selectedFilter.toLowerCase();
    return fileList.filter((f) => {
      const s = String(f.scope || "").toLowerCase();
      if (selectedFilter === "Overview") {
        return !s.includes("scope 1") && !s.includes("scope 2") && !s.includes("scope 3");
      }
      return s.includes(filterLower);
    });
  }, [fileList, selectedFilter]);

  const handleSelectionChanged = useCallback((e) => {
    const selected = e.api.getSelectedRows();
    setSelectedFiles(selected);
  }, []);

  const handleDirectFileUpload = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const oversized = files.filter((f) => f.size > MAX_FILE_SIZE_BYTES);
    if (oversized.length > 0) {
      enqueueAlert({
        message: t("File exceeds 5MB limit: {{names}}", {
          names: oversized.map((f) => f.name).join(", "),
        }),
        severity: "error",
        duration: 5000,
      });
      return;
    }

    const targetScope = selectedFilter !== "All" ? selectedFilter : "Scope 1";
    let idColumnName = "organization_id";
    if (pageType === "supply_chain") idColumnName = "supply_chain_id";
    else if (pageType === "corporate") idColumnName = "corporate_id";
    else if (pageType === "valueChain") idColumnName = "product_service_id";

    const formData = new FormData();
    formData.append("rowID", selectedRow?.id || "unknown");
    formData.append("scope", targetScope);
    formData.append("IDColumnName", idColumnName);

    files.forEach((file) => formData.append("files", file));

    setIsUploading(true);
    fetchWithToken(`${config.apiUrl}/api/upload_emission_reference_files`, {
      method: "POST",
      body: formData,
    })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data?.error || t("Failed to upload evidence files"));
        if (selectedRow?.id) fetchFiles(selectedRow.id);
        enqueueAlert({
          message: t("Successfully uploaded {{count}} file(s) to {{scope}}", {
            count: files.length,
            scope: targetScope,
          }),
          severity: "success",
          duration: 3500,
        });
      })
      .catch((err) => {
        console.error("Direct upload error:", err);
        enqueueAlert({
          message: err.message || t("Error uploading evidence files"),
          severity: "error",
          duration: 4000,
        });
      })
      .finally(() => {
        setIsUploading(false);
      });
  };

  const handleDownloadSelected = useCallback(() => {
    if (selectedFiles.length === 0) return;
    selectedFiles.forEach((file) => {
      if (file.url) {
        window.open(file.url, "_blank");
      }
    });
  }, [selectedFiles]);

  const handleDeleteSelected = useCallback(() => {
    if (selectedFiles.length === 0) return;

    const fileIds = selectedFiles.map((f) => f.id);
    const fileUrls = selectedFiles.map((f) => f.url);

    fetchWithToken(`${config.apiUrl}/api/delete_emission_reference_files`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ file_ids: fileIds, file_urls: fileUrls }),
    })
      .then((res) => res.json())
      .then(() => {
        if (selectedRow?.id) fetchFiles(selectedRow.id);
        gridRef.current?.api?.deselectAll();
        setSelectedFiles([]);
        enqueueAlert({
          message: t("Selected evidence files deleted successfully"),
          severity: "success",
          duration: 3000,
        });
      })
      .catch((err) => {
        console.error("Delete evidence files error:", err);
        enqueueAlert({
          message: t("Failed to delete selected evidence files"),
          severity: "error",
          duration: 4000,
        });
      });
  }, [selectedFiles, selectedRow?.id, fetchFiles, enqueueAlert, t]);

  const handleDeleteSingleFile = useCallback(
    (file) => {
      if (!file?.id) return;
      fetchWithToken(`${config.apiUrl}/api/delete_emission_reference_files`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ file_ids: [file.id], file_urls: [file.url] }),
      })
        .then((res) => res.json())
        .then(() => {
          if (selectedRow?.id) fetchFiles(selectedRow.id);
          enqueueAlert({
            message: t("File deleted successfully"),
            severity: "success",
            duration: 3000,
          });
        })
        .catch((err) => {
          console.error("Delete file error:", err);
          enqueueAlert({
            message: t("Failed to delete file"),
            severity: "error",
            duration: 4000,
          });
        });
    },
    [selectedRow?.id, fetchFiles, enqueueAlert, t]
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Sl.No",
        valueGetter: (params) => params.node.rowIndex + 1,
        width: 65,
        minWidth: 55,
        maxWidth: 85,
        ...defaultColDef,
        ...rightAlignColDef,
        cellStyle: { ...rightAlignColDef.cellStyle, paddingRight: "16px" },
        suppressMovable: true,
        sortable: false,
      },
      {
        headerName: t("Document File Name"),
        field: "name",
        flex: 2,
        minWidth: 240,
        ...defaultColDef,
        ...leftAlignColDef,
        headerCheckboxSelection: true,
        checkboxSelection: true,
        cellRenderer: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%", gap: 1, overflow: "hidden" }}>
            <LuFileText size={16} color="#d97706" style={{ flexShrink: 0 }} />
            <Typography
              component="a"
              href={params.data?.url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                if (!params.data?.url) e.preventDefault();
              }}
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "13px",
                fontWeight: 500,
                color: "var(--m3-primary, #4a7c23)",
                textDecoration: "none",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                cursor: params.data?.url ? "pointer" : "default",
                "&:hover": {
                  textDecoration: params.data?.url ? "underline" : "none",
                },
              }}
            >
              {params.value || t("Untitled Document")}
            </Typography>
          </Box>
        ),
      },
      {
        headerName: t("Scope / Category"),
        field: "scope",
        flex: 1.4,
        minWidth: 170,
        ...defaultColDef,
        ...centerAlignColDef,
        cellRenderer: (params) => {
          const sc = String(params.value || "Overview");
          const scLower = sc.toLowerCase();
          let tone = "neutral";
          if (scLower.includes("scope 1")) tone = "error";
          else if (scLower.includes("scope 2")) tone = "warning";
          else if (scLower.includes("scope 3")) tone = "info";
          else tone = "primary";

          return (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", width: "100%" }}>
              <M3Badge tone={tone}>{sc}</M3Badge>
            </Box>
          );
        },
      },
      {
        headerName: t("Verification Status"),
        field: "status",
        flex: 1.1,
        minWidth: 150,
        ...defaultColDef,
        ...centerAlignColDef,
        cellRenderer: () => (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", width: "100%" }}>
            <M3Badge tone="success">{t("Attached & Verified")}</M3Badge>
          </Box>
        ),
      },
      {
        headerName: t("Upload Date & Time"),
        field: "uploaded_at",
        flex: 1.2,
        minWidth: 160,
        ...defaultColDef,
        ...centerAlignColDef,
        valueFormatter: (params) => formatUploadedAt(params.value),
      },
      {
        headerName: t("Actions"),
        field: "actions",
        width: 110,
        minWidth: 100,
        ...defaultColDef,
        ...centerAlignColDef,
        cellRenderer: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", gap: 0.5 }}>
            <Tooltip title={t("Download Document")}>
              <span>
                <M3IconButton
                  size="sm"
                  variant="outlined"
                  icon={<LuDownload size={14} />}
                  onClick={() => {
                    if (params.data?.url) window.open(params.data.url, "_blank");
                  }}
                  aria-label={t("Download")}
                />
              </span>
            </Tooltip>
            <Tooltip title={t("Delete Document")}>
              <span>
                <M3IconButton
                  size="sm"
                  variant="outlined"
                  icon={<FiTrash2 size={14} />}
                  onClick={() => handleDeleteSingleFile(params.data)}
                  aria-label={t("Delete")}
                  style={{
                    color: "#dc2626",
                    borderColor: "rgba(220, 38, 38, 0.25)",
                  }}
                />
              </span>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [t, handleDeleteSingleFile]
  );

  return (
    <Box>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        multiple
        accept={ACCEPTED_TYPES}
        onChange={(e) => {
          handleDirectFileUpload(e);
          e.target.value = null;
        }}
      />

      {/* Scope Filter Chips */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
        {SCOPE_FILTERS.map((scopeKey) => {
          const active = selectedFilter === scopeKey;
          const count = counts[scopeKey] || 0;
          return (
            <Chip
              key={scopeKey}
              label={`${scopeKey} (${count})`}
              selected={active}
              onClick={() => setSelectedFilter(scopeKey)}
            />
          );
        })}
      </Box>

      <DataTableCard
        title={t("Evidence Repository")}
        subtitle={
          fileList.length > 0
            ? t("{{count}} evidence documents attached · Max 5MB per file", { count: fileList.length })
            : t("Max 5MB · .pdf · .csv · .xlsx · .doc · .docx · .jpg · .png")
        }
        actions={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <M3Button
              variant="tonalPrimary"
              size="sm"
              onClick={() => setIsBatchAnalyzerOpen(true)}
              icon={<AutoAwesomeOutlinedIcon sx={{ fontSize: 16 }} />}
            >
              {t("Upload & Analyze Evidence")}
            </M3Button>
            <M3Button
              variant="outlined"
              size="sm"
              disabled={isUploading}
              onClick={() => fileInputRef.current?.click()}
              icon={<FiUpload size={16} />}
            >
              {isUploading ? t("Uploading...") : t("Upload Document")}
            </M3Button>
            {selectedFiles.length > 0 && (
              <>
                <Tooltip title={t("Download Selected ({{count}})", { count: selectedFiles.length })}>
                  <span>
                    <M3IconButton
                      variant="outlined"
                      size="sm"
                      onClick={handleDownloadSelected}
                      icon={<LuDownload size={16} />}
                      aria-label={t("Download Selected")}
                    />
                  </span>
                </Tooltip>
                <Tooltip title={t("Delete Selected ({{count}})", { count: selectedFiles.length })}>
                  <span>
                    <M3IconButton
                      variant="outlined"
                      size="sm"
                      onClick={handleDeleteSelected}
                      icon={<FiTrash2 size={16} />}
                      aria-label={t("Delete Selected")}
                      style={{
                        color: "#dc2626",
                        borderColor: "rgba(220, 38, 38, 0.25)",
                      }}
                    />
                  </span>
                </Tooltip>
              </>
            )}
          </Box>
        }
      >
        {fileList.length === 0 ? (
          <Box
            sx={{
              p: 6,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "12px",
              border: `1px dashed ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"}`,
              bgcolor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)",
              my: 2,
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                bgcolor: "var(--m3-primary-container, #dcfce7)",
                color: "var(--m3-on-primary-container, #166534)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <LuInbox size={26} />
            </Box>
            <Typography
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "16px",
                fontWeight: 600,
                color: "var(--m3-on-surface, #0f172a)",
                mb: 0.75,
              }}
            >
              {t("No Evidence Documents Attached Yet")}
            </Typography>
            <Typography
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "13px",
                color: "text.secondary",
                maxWidth: 460,
                mb: 3,
                lineHeight: 1.5,
              }}
            >
              {t(
                "Upload utility bills, fuel delivery slips, freight waybills, or vendor invoices to automatically extract data and establish a verified audit trail."
              )}
            </Typography>
            <M3Button
              variant="filled"
              onClick={() => setIsBatchAnalyzerOpen(true)}
              icon={<AutoAwesomeOutlinedIcon sx={{ fontSize: 16 }} />}
            >
              {t("Upload & Analyze First Document")}
            </M3Button>
          </Box>
        ) : (
          <AgGrid
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            headerCheckbox={false}
            pagination={true}
            paginationPageSize={10}
            onSelectionChanged={handleSelectionChanged}
            domLayout="autoHeight"
            sx={{
              "& .ag-cell-wrapper .ag-selection-checkbox": {
                marginRight: "8px !important",
              },
            }}
          />
        )}
      </DataTableCard>

      {isBatchAnalyzerOpen && (
        <EvidenceBillBatchAnalyzerModal
          open={isBatchAnalyzerOpen}
          onClose={() => setIsBatchAnalyzerOpen(false)}
          scope={selectedFilter !== "All" ? selectedFilter : "Scope 1"}
          existingFiles={fileList}
          organizationID={selectedRow?.id}
          organizationType={
            pageType === "supply_chain"
              ? "Supply_chain"
              : pageType === "corporate"
              ? "Corporate"
              : "Structure"
          }
          pageType={pageType}
          onSuccess={() => {
            if (selectedRow?.id) fetchFiles(selectedRow.id);
            setRefresh?.((r) => !r);
          }}
        />
      )}
    </Box>
  );
}

export default DocumentViewer;
